import React, { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/PressableButton";

const initialFormState = {
  campaignId: "",
  campaignName: "",
  mode: "daily",
  state: "ENABLED",
  budget: "",
  hour: "",
  minute: "",
  weekdays: [],
  scheduledDateTime: "",
};

const ScheduleForm = ({ onSubmit, initialData, onCancel, campaigns = [] }) => {
  const [form, setForm] = useState(initialFormState);
  const [campaignFilter, setCampaignFilter] = useState("all");
  const fileInputRef = useRef(null);

  // ===== CSV 操作用: DL/UP ハンドラ =====
  const API = process.env.REACT_APP_API_URL;
  const token = () => localStorage.getItem("token");

  // 認証ヘッダ付きでCSVをダウンロード
  const downloadCsv = async (path, filename) => {
    try {
      const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error("ダウンロードに失敗しました");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "ダウンロードに失敗しました");
    }
  };

  // CSVアップロード
  const handleUploadCsv = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/csv/schedules/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
        body: fd, // Content-Typeは付けない
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "アップロード失敗");
      if (data.errors?.length) {
        console.table(data.errors.slice(0, 20)); // 先頭20件をブラウザConsoleに表示
      }
      alert(`作成:${data.created} 更新:${data.updated} スキップ:${data.skipped}\nエラー:${data.errors?.length || 0}`);
      window.location.reload(); // 一覧を即反映
    } catch (e) {
      alert(e.message || "アップロードに失敗しました");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  // ================================

  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialData,
        budget: initialData.budget ?? "",
        hour: initialData.hour ?? "",
        minute: initialData.minute ?? "",
        weekdays: initialData.weekdays ?? [],
        scheduledDateTime: initialData.scheduledDateTime ?? "",
      });
    }
  }, [initialData]);

  if (!Array.isArray(campaigns)) {
    console.warn("⚠ ScheduleForm: campaigns が不正です", campaigns);
    return <p>キャンペーン情報を取得中です...</p>;
  }

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setCampaignFilter(value);
    localStorage.setItem("campaignFilter", value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleWeekdayChange = (day) => {
    setForm((prev) => {
      const exists = prev.weekdays.includes(day);
      return {
        ...prev,
        weekdays: exists ? prev.weekdays.filter((d) => d !== day) : [...prev.weekdays, day],
      };
    });
  };

  const handleSelectCampaign = (e) => {
    const selectedId = e.target.value;
    const selected = campaigns.find((c) => c.campaignId === selectedId || c.id === selectedId);
    setForm((prev) => ({
      ...prev,
      campaignId: selected?.campaignId || selected?.id || "",
      campaignName: selected?.name || "",
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.budget && Number(form.budget) < 100) {
      alert("予算は100円以上で入力してください");
      return;
    }
    onSubmit(form);
    setForm(initialFormState);
  };

  const handleCancel = () => {
    onCancel?.();
    setForm(initialFormState);
  };

  const getFilteredCampaigns = () => {
    return campaigns.filter((c) => {
      if (campaignFilter === "all") return true;
      if (!c.mode) return false;
      return c.mode?.toLowerCase()?.trim() === campaignFilter;
    });
  };

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <form onSubmit={handleSubmit} style={{ paddingTop: "10px" /* ツールバー分の余白 */ }}>
        <div style={{ marginBottom: "8px" }}>
          <label style={{ marginRight: "8px" }}>表示：</label>
          <label>
            <input
              type="radio"
              name="campaignFilter"
              value="all"
              checked={campaignFilter === "all"}
              onChange={handleFilterChange}
            />
            すべて
          </label>
          <label style={{ marginLeft: "8px" }}>
            <input
              type="radio"
              name="campaignFilter"
              value="auto"
              checked={campaignFilter === "auto"}
              onChange={handleFilterChange}
            />
            オート
          </label>
          <label style={{ marginLeft: "8px" }}>
            <input
              type="radio"
              name="campaignFilter"
              value="manual"
              checked={campaignFilter === "manual"}
              onChange={handleFilterChange}
            />
            マニュアル
          </label>
        </div>

        <div>
          <label>キャンペーン：</label>
          <select value={form.campaignId} onChange={handleSelectCampaign}>
            <option value="">-- キャンペーンを選択 --</option>
            {getFilteredCampaigns().map((c) => (
              <option key={c.campaignId || c.id} value={c.campaignId || c.id}>
                {c.name || "(名前なし)"}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>モード：</label>
          <label>
            <input
              type="radio"
              name="mode"
              value="daily"
              checked={form.mode === "daily"}
              onChange={handleChange}
            />
            毎日
          </label>
          <label>
            <input
              type="radio"
              name="mode"
              value="once"
              checked={form.mode === "once"}
              onChange={handleChange}
            />
            1回のみ
          </label>
        </div>

        <div>
          <label>操作：</label>
          <select name="state" value={form.state} onChange={handleChange}>
            <option value="ENABLED">ON</option>
            <option value="PAUSED">OFF</option>
          </select>
        </div>

        <div>
          <label>予算 (任意)：</label>
          <input
            name="budget"
            type="number"
            value={form.budget}
            onChange={handleChange}
            style={{ width: "60px" }}
          />
        </div>

        {form.mode === "daily" && (
          <>
            <div>
              <label>時：</label>
              <input
                name="hour"
                type="number"
                min="0"
                max="23"
                value={form.hour}
                onChange={handleChange}
                style={{ width: "35px" }}
              />
            </div>
            <div>
              <label>分：</label>
              <input
                name="minute"
                type="number"
                min="0"
                max="59"
                value={form.minute}
                onChange={handleChange}
                style={{ width: "35px" }}
              />
            </div>
            <div>
              <label>曜日：</label>
              {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                <label key={day}>
                  <input
                    type="checkbox"
                    checked={form.weekdays.includes(day)}
                    onChange={() => handleWeekdayChange(day)}
                  />
                  {"日月火水木金土"[day]}
                </label>
              ))}
            </div>
          </>
        )}

        {form.mode === "once" && (
          <div>
            <label>実行日時：</label>
            <input
              name="scheduledDateTime"
              type="datetime-local"
              value={form.scheduledDateTime}
              onChange={handleChange}
            />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", marginTop: "12px" }}>
          <Button type="submit" variant="default">{initialData ? "更新" : "登録"}</Button>
          {initialData && <Button type="button" variant="gray" onClick={handleCancel}>キャンセル</Button>}
        </div>

        {/* CSV 操作ボタン行 */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "10px",
            justifyContent: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <Button
            type="button"
            variant="default"
            onClick={() => downloadCsv("/api/csv/campaigns", "adsche_new_template.csv")}
            title="非ARCHIVEDの最新キャンペーンCSVをダウンロード"
          >
            新規CSV
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => downloadCsv("/api/csv/schedules", "adsche_update.csv")}
            title="登録済みスケジュールCSVをダウンロード"
          >
            更新CSV
          </Button>

          <Button
            type="button"
            variant="success"
            onClick={() => fileInputRef.current?.click()}
            title="CSVをアップロードして登録/更新"
          >
            CSVアップロード
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e) => handleUploadCsv(e.target.files?.[0])}
          />
        </div>
      </form>
    </div>
  );
};

export default ScheduleForm;